/**
 * Math PURE du zoom/pan de la visionneuse (aucune dépendance DOM → testable).
 *
 * Modèle : `scale` = ratio taille_affichée / taille_native (donc `1` = 1:1
 * pixel natif, « 100 % »). L'`offset` est un décalage en pixels écran appliqué
 * après centrage (translation de panoramique).
 */

/**
 * L'image occupe au repos 100 % de la frame (fit « contain » : elle touche le
 * bord largeur OU hauteur, le premier atteint). Les grandes images remplissent
 * toute la fenêtre disponible.
 */
export const FRAME_FILL = 1.0;
/**
 * Plafond d'agrandissement au repos PAR DÉFAUT pour une image PLUS PETITE que la boîte
 * cible : on ne la gonfle que de +50 % (au-delà, l'interpolation dégrade trop — on
 * préserve la qualité plutôt que de gonfler pour remplir la frame).
 *
 * C'est le REPLI : la valeur effective est un RÉGLAGE GLOBAL (superadmin, console
 * « Général » → `app_settings.small_image_upscale_percent`, défaut
 * `SMALL_IMAGE_UPSCALE_DEFAULT_PERCENT = 150` partagé). `ZoomableImage` lit ce réglage
 * via `useAppConfig` et le passe à `computeZoomBounds` ; ce défaut ne sert qu'aux
 * appelants directs (tests) et tant que la config n'est pas chargée.
 */
export const MAX_INITIAL_UPSCALE = 1.5;
/** Zoom manuel maximal (ratio à la taille native) — pour inspecter le grain. */
export const MAX_ZOOM = 6;
/** Facteur multiplicatif par cran de molette / bouton. */
export const ZOOM_STEP = 1.25;

export interface ZoomBounds {
  /** Échelle qui fait tenir l'image dans 100 % de la frame (contain). */
  base: number;
  /** Vue par défaut : `base` plafonnée à `MAX_INITIAL_UPSCALE` pour les petites images. */
  initial: number;
  /** Borne basse du zoom (= vue par défaut : inutile d'afficher plus petit). */
  min: number;
  /** Borne haute du zoom. */
  max: number;
}

export function clamp(value: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, value));
}

/**
 * Bornes de zoom pour une image `natural` dans un conteneur donné. `null` si une
 * dimension est inconnue/nulle (le conteneur n'est pas encore mesuré, ou pas de
 * dimensions) → l'appelant retombe alors sur un rendu `object-contain` neutre.
 */
export function computeZoomBounds(
  naturalW: number,
  naturalH: number,
  containerW: number,
  containerH: number,
  /**
   * Plafond d'agrandissement au repos des petites images (ratio, ex. `1.5` = +50 %).
   * Défaut `MAX_INITIAL_UPSCALE` ; en pratique `ZoomableImage` passe le réglage global
   * (`smallImageUpscalePercent / 100`).
   */
  maxInitialUpscale: number = MAX_INITIAL_UPSCALE,
): ZoomBounds | null {
  if (naturalW <= 0 || naturalH <= 0 || containerW <= 0 || containerH <= 0) return null;
  const base = Math.min((FRAME_FILL * containerW) / naturalW, (FRAME_FILL * containerH) / naturalH);
  const initial = Math.min(base, maxInitialUpscale);
  return { base, initial, min: initial, max: Math.max(initial, MAX_ZOOM) };
}

/** Débordement maximal (px) sur un axe : moitié de ce qui dépasse le conteneur. */
export function maxOffset(displaySize: number, containerSize: number): number {
  return Math.max(0, (displaySize - containerSize) / 2);
}

/** Contraint un décalage de panoramique pour que l'image ne quitte pas le cadre. */
export function clampOffset(offset: number, displaySize: number, containerSize: number): number {
  const m = maxOffset(displaySize, containerSize);
  return clamp(offset, -m, m);
}

/** Un axe est panoramable si l'image affichée déborde du conteneur. */
export function isPannable(
  displayW: number,
  displayH: number,
  containerW: number,
  containerH: number,
): boolean {
  return displayW - containerW > 0.5 || displayH - containerH > 0.5;
}

/**
 * Nouveau décalage sur un axe pour garder le point sous le curseur STABLE lors
 * d'un zoom de facteur `k = nouvelleÉchelle / ancienneÉchelle`. `cursor` et
 * `offset` sont exprimés relativement au CENTRE du conteneur.
 *
 * Dérivation : le point image sous le curseur vérifie `cursor = offset + s·u`
 * (u = coord. image). Après zoom, on veut `cursor = offset' + s'·u`, d'où
 * `offset' = cursor − k·(cursor − offset)`.
 */
export function anchorOffset(offset: number, cursor: number, k: number): number {
  return cursor - k * (cursor - offset);
}

/**
 * Décalage d'axe pour un PINCER à deux doigts : garde le point image qui était
 * sous le MILIEU INITIAL des doigts (`m0`) sous le milieu COURANT (`m1`), lors
 * d'un zoom de facteur `k = nouvelleÉchelle / ancienneÉchelle`. Généralise
 * `anchorOffset` à un milieu MOBILE → gère aussi le PAN à deux doigts (déplacer
 * les deux doigts sans changer l'écartement translate l'image). `m0`/`m1`/`offset`
 * sont relatifs au CENTRE du conteneur.
 *
 * Dérivation : le point image sous `m0` vérifie `m0 = offset + s·u`. On veut
 * `m1 = offset' + k·s·u`, d'où `offset' = m1 − k·(m0 − offset)`. Quand `m1 = m0`,
 * on retrouve exactement `anchorOffset(offset, m0, k)`.
 */
export function pinchOffset(offset: number, m0: number, m1: number, k: number): number {
  return m1 - k * (m0 - offset);
}
